<script setup lang="ts">
import { useAuthStore } from '#modules/auth/store/auth';
import { useClipboard } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import calendarIcon from '~/assets/img/icon/calendar.svg';
import closeIcon from '~/assets/img/icon/close.svg';
import mapPinIcon from '~/assets/img/icon/map-pin.svg';
import shareIcon from '~/assets/img/icon/share.svg';
import userIcon from '~/assets/img/icon/user.svg';
import { AppButton, AppTypography } from '~/components/base';
import BidDetails from '~/components/BidDetails.vue';
import ImageGallery from '~/components/ImageGallery.vue';
import ReportListing from '~/components/ReportListing.vue';
import SubmitTenderForm from '~/components/SubmitTenderForm.vue';
import type { Bid } from '~/types/Bid';
import type { Job } from '~/types/Job';
import type { User } from '~/types/User';
import { formatDate } from '~/utils/date';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    jobId?: number | null;
    bids?: boolean;
  }>(),
  {
    jobId: null,
    bids: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { hasLoggedInUser, userRole, user } = storeToRefs(useAuthStore());
const { $api } = useNuxtApp();
const requestUrl = useRequestURL();
const { copy, copied } = useClipboard();

// Check if user is a contractor (only contractors can submit tenders)
const isContractor = computed(() => userRole.value === 'CONTRACTOR');

// Fetch job details
const job = ref<Job | null>(null);
const bid = ref<Bid>();
const loading = ref(false);

// Lightbox state
const scrollPosition = ref(0);
const originalBodyOverflow = ref<string | null>(null);
const originalHtmlOverflow = ref<string | null>(null);

const fetchJobDetails = async () => {
  if (!props.jobId) {
    return;
  }
  loading.value = true;
  try {
    const url = props.bids ? `/jobs/${props.jobId}/my-bid` : `/guest/jobs/${props.jobId}`;
    const response = await $api<{ data: Job | Bid }>(url);
    if (props.bids) {
      bid.value = response.data as Bid;
      job.value = bid.value.job_post as Job;
    } else {
      job.value = response.data as Job;
    }
  } catch (error) {
    job.value = null;
  } finally {
    loading.value = false;
  }
};

// Watch for jobId changes and fetch job details when drawer opens
watch(
  () => [props.jobId, props.modelValue],
  ([newJobId, isOpen]) => {
    if (isOpen && newJobId) {
      fetchJobDetails();
    }
  },
  { immediate: true }
);

// Computed property for formatted rate display

const lockBodyScroll = () => {
  if (!process.client) {
    return;
  }

  scrollPosition.value = window.scrollY || 0;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollPosition.value}px`;
  document.body.style.width = '100%';

  if (originalBodyOverflow.value === null) {
    originalBodyOverflow.value = document.body.style.overflow;
  }
  if (originalHtmlOverflow.value === null) {
    originalHtmlOverflow.value = document.documentElement.style.overflow;
  }

  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
};

const unlockBodyScroll = () => {
  if (!process.client) {
    return;
  }

  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  window.scrollTo(0, scrollPosition.value);

  if (originalBodyOverflow.value !== null) {
    document.body.style.overflow = originalBodyOverflow.value;
    originalBodyOverflow.value = null;
  } else {
    document.body.style.overflow = '';
  }

  if (originalHtmlOverflow.value !== null) {
    document.documentElement.style.overflow = originalHtmlOverflow.value;
    originalHtmlOverflow.value = null;
  } else {
    document.documentElement.style.overflow = '';
  }
};

const drawerModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

watch(
  drawerModel,
  (isOpen) => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  unlockBodyScroll();
});

const closeDrawer = () => {
  drawerModel.value = false;
};

const shareJob = () => {
  if (job.value) {
    copy(requestUrl.host + requestUrl.pathname + '?job=' + job.value.id);
  }
};

const getStatusColor = (status: string = '') => {
  switch (status) {
    case 'pending':
      return { bg: '#FFEDD3', text: '#503627' };
    case 'accepted':
      return { bg: '#DAE8CD', text: '#182E1E' };
    case 'unsuccessful':
      return { bg: '#FCCCD3', text: '#4C0427' };
    default:
      return { bg: '#F5F5F5', text: '#757575' };
  }
};

const getListingStatusColor = (status: string = '') => {
  switch (status) {
    case 'open':
      return { bg: '#DAE8CD', text: '#182E1E' };
    case 'assigned':
      return { bg: '#EEF4FA', text: '#01011D' };
    default:
      return { bg: '#E6E6E8', text: '#01011D' };
  }
};
</script>

<template>
  <Teleport to="body">
    <v-navigation-drawer
      v-if="drawerModel"
      v-model="drawerModel"
      location="right"
      temporary
      :width="$vuetify.display.width <= 583 ? '100%' : 583"
      :class="'listing-drawer ' + (!bids ? 'top-0' : '')"
    >
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <v-progress-circular indeterminate color="primary" size="64" />
      </div>

      <!-- Job Content -->
      <div v-else-if="job" class="pa-6 mb-16">
        <!-- Header -->
        <div class="d-flex justify-space-between align-center mb-6">
          <v-btn variant="outlined" class="header-btn-icon d-sm-none" size="x-small" @click.stop="closeDrawer">
            <icon name="ewc:arrow-left" size="24" />
          </v-btn>
          <div></div>
          <div class="d-flex gap-x-2">
            <v-btn variant="outlined" class="mr-sm-2 header-btn" size="small" @click="shareJob" v-if="!bids">
              <img v-if="!copied" :src="shareIcon" alt="Share" width="24" height="24" />
              <span v-if="!copied" class="ml-2">Share</span>
              <span v-if="copied">Copied ✓</span>
            </v-btn>
            <v-btn
              variant="outlined"
              class="header-btn-icon d-none d-sm-block"
              size="x-small"
              @click.stop="closeDrawer"
            >
              <img :src="closeIcon" alt="Close" width="24" height="24" />
            </v-btn>
          </div>
        </div>
        <div class="mb-4">
          <v-chip
            class="text-uppercase font-weight-bold status-chip"
            size="small"
            variant="flat"
            v-if="!bids"
            :style="{
              backgroundColor: getListingStatusColor(job.status).bg,
              color: getListingStatusColor(job.status).text,
            }"
          >
            <AppTypography element="p" type="subheadline-2" class="font-weight-bold">{{ job.status }}</AppTypography>
          </v-chip>

          <v-chip
            class="text-uppercase font-weight-bold status-chip"
            size="small"
            variant="flat"
            v-if="bids"
            :style="{
              backgroundColor: getStatusColor(bid?.status).bg,
              color: getStatusColor(bid?.status).text,
            }"
          >
            <AppTypography element="p" type="subheadline-2" class="font-weight-bold">{{ bid?.status }}</AppTypography>
          </v-chip>
        </div>

        <!-- Job Details -->
        <h2 class="text-h5 font-weight-bold mb-3">{{ job.name }}</h2>
        <p class="text-body-1 mb-4">{{ job.description }}</p>

        <div class="mb-2">
          <img :src="mapPinIcon" alt="Location" width="28" height="28" class="mr-1" style="vertical-align: middle" />
          <span>{{ job.location_name }}</span>
        </div>

        <div class="mb-2">
          <img :src="calendarIcon" alt="Calendar" width="24" height="24" class="mr-2" style="vertical-align: middle" />
          <span
            >Needed By:
            {{
              job.timeline_preference === 'choose_date'
                ? formatDate(job.timeline_date as string, 'DD MMMM YYYY')
                : job.timeline_preference_text
            }}
          </span>
        </div>

        <div class="mb-6">
          <img :src="userIcon" alt="User" width="24" height="24" class="mr-2" style="vertical-align: middle" />
          <span>Posted by: {{ job?.user?.first_name }} {{ job?.user?.last_name.charAt(0) }}.</span>
        </div>

        <!-- Images -->
        <ImageGallery :photos="job?.photos" />

        <!-- Divider -->
        <v-divider class="my-6" />

        <!-- Submit Tender Form (Contractors Only) -->
        <SubmitTenderForm
          v-if="hasLoggedInUser && isContractor && !bids && user.id !== job.user.id"
          :job-id="jobId as number"
          @close-confirmation-dialog-action="closeDrawer"
        />

        <!-- Bid Details -->
        <BidDetails v-if="hasLoggedInUser && bids" :bid-details="bid as Bid" :job-poster="job.user as User" />

        <!-- Login to Submit Tender (Guest Only) -->
        <v-card v-if="!hasLoggedInUser" class="pa-6 bg-grey-lighten-4" flat>
          <div class="d-flex flex-column align-center">
            <h3 class="text-h5 font-weight-bold mb-2">Want to submit an Offer?</h3>
            <p class="text-body-2 text-grey-darken-1 mb-6">
              Join the platform to send your offer and availability for this job.
            </p>
          </div>

          <div class="d-flex flex-column flex-sm-row ga-4">
            <div class="flex w-100">
              <AppButton color="yellow-darken-1" variant="flat" block @click="navigateTo('/register')">
                Sign Up
              </AppButton>
            </div>
            <div class="flex w-100">
              <AppButton color="" variant="outlined" block @click="navigateTo('/login')"> Login</AppButton>
            </div>
          </div>
        </v-card>

        <!-- Report Listing -->
        <ReportListing :job-id="jobId as number" />
      </div>
    </v-navigation-drawer>
  </Teleport>
</template>

<style scoped lang="scss">
@use '#core/assets/scss/partials/fonts' as *;
@use '#core/assets/scss/partials/colors' as *;

.listing-drawer {
  height: 100vh !important;
  position: fixed !important;

  :deep(.v-navigation-drawer__content) {
    overflow-y: auto;
    height: 100vh;
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100vh;
    padding: 24px;
    box-sizing: border-box;
  }

  @media (max-width: 768px) {
    .loading-state {
      padding: 0;
      width: 100vw;
    }
  }

  h2,
  h3 {
    font-family: $font-family-display;
  }

  .v-chip {
    font-weight: 700;
  }

  .status-chip {
    padding: 12px 16px !important;
    border-radius: 8px !important;
    height: 40px !important;
    min-height: 40px !important;
  }

  .header-btn {
    height: 40px !important;
    min-height: 40px !important;
    border-color: #e6e6e8 !important;

    :deep(.v-btn__overlay) {
      border-color: #e6e6e8 !important;
    }
  }

  .header-btn-icon {
    height: 40px !important;
    min-height: 40px !important;
    width: 40px !important;
    border-color: #e6e6e8 !important;

    :deep(.v-btn__overlay) {
      border-color: #e6e6e8 !important;
    }
  }
}

:deep(.v-overlay--active) {
  z-index: 9998 !important;
}

// Disabled button styling
:deep(.v-btn--disabled) {
  background-color: #e0e0e0 !important;

  .v-btn__content {
    color: #01011d !important;
  }

  // Specific styling for AppButton when disabled
  &.v-btn--variant-flat {
    background-color: #e0e0e0 !important;

    .v-btn__content {
      color: #01011d !important;
    }
  }
}

// Rate input styling
.rate-input {
  :deep(input) {
    font-size: 24px !important;
    font-weight: 700 !important;
  }
}

// Checkbox card styling to match AppTextField
.checkbox-card {
  background-color: white !important;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  border-radius: 8px !important;
  padding: 6px 16px !important;
  box-shadow: none !important;

  :deep(.v-checkbox) {
    margin: 0 !important;
  }
}
</style>
